import { bind } from '@react-rxjs/core'
import { BehaviorSubject, distinctUntilChanged, Observable, scan } from 'rxjs'

export interface DispensaryCartInitial {
  type: 'cart_initial'
}

/**
 * Published before the first cart request of a hydration that replaces the cart's items. The steps
 * below each await their inputs (the settled cart, the current attributes) before publishing, so
 * without this the status reads not-loading for the whole opening span — over a cart that is about
 * to be emptied and refilled.
 *
 * Only that kind of hydration announces itself. A hydration that merely reconciles cart attributes
 * publishes nothing at all, because every step below stays silent when it has no work, and the
 * shopper keeps their loaded cart rather than watching a skeleton over it.
 */
export interface DispensaryCartHydrationStart {
  type: 'cart_hydration_start'
}

export interface DispensaryCartEmptyStart {
  type: 'cart_empty_start'
}

export interface DispensaryCartEmptyComplete {
  type: 'cart_empty_complete'
}

export interface DispensaryCartAddProductsStart {
  type: 'cart_add_products_start'
  products: {
    sku: string
    quantity: number
  }[]
}

export interface DispensaryCartAddProductsComplete {
  type: 'cart_add_products_complete'
  products: {
    sku: string
    quantity: number
  }[]
}

export interface DispensaryCartRemovePromoCodeStart {
  type: 'cart_remove_promo_code_start'
  promoCodes: string[]
}

export interface DispensaryCartRemovePromoCodeComplete {
  type: 'cart_remove_promo_code_complete'
  promoCodes: string[]
}

export interface DispensaryCartApplyPromoCodeStart {
  type: 'cart_apply_promo_code_start'
  promoCode: string
}

export interface DispensaryCartApplyPromoCodeComplete {
  type: 'cart_apply_promo_code_complete'
  promoCode: string
}

export interface DispensaryCartSetDispensaryPromoCodeStart {
  type: 'cart_set_dispensary_promo_code_start'
  dispensaryPromoCode: string
}

export interface DispensaryCartSetDispensaryPromoCodeComplete {
  type: 'cart_set_dispensary_promo_code_complete'
  dispensaryPromoCode: string
}

export interface DispensaryCartUpdateAttributesStart {
  type: 'cart_update_attributes_start'
  practitionerCode?: string | null
  wellWorldId?: string | null
  wellWorldStoreId?: string | null
  wellWorldPracticeId?: string | null
}

export interface DispensaryCartUpdateAttributesComplete {
  type: 'cart_update_attributes_complete'
  practitionerCode?: string | null
  wellWorldId?: string | null
  wellWorldStoreId?: string | null
  wellWorldPracticeId?: string | null
}

export interface DispensaryCartFinal {
  type: 'cart_final'
}

export interface DispensaryCartError {
  type: 'cart_error'
  error: Error
}

export type DispensaryCartState =
  | DispensaryCartInitial
  | DispensaryCartHydrationStart
  | DispensaryCartEmptyStart
  | DispensaryCartEmptyComplete
  | DispensaryCartAddProductsStart
  | DispensaryCartAddProductsComplete
  | DispensaryCartRemovePromoCodeStart
  | DispensaryCartRemovePromoCodeComplete
  | DispensaryCartApplyPromoCodeStart
  | DispensaryCartApplyPromoCodeComplete
  | DispensaryCartSetDispensaryPromoCodeStart
  | DispensaryCartSetDispensaryPromoCodeComplete
  | DispensaryCartUpdateAttributesStart
  | DispensaryCartUpdateAttributesComplete
  | DispensaryCartFinal
  | DispensaryCartError

/**
 * Shared by the subject's seed, the reset, and the derived status below, deliberately as one
 * reference: the status is compared field by field on identity, so a fresh `{ ...initialState }` in
 * any of those places reports a second, equal `cart_initial` that cannot be collapsed. Never mutated.
 */
const initialState: DispensaryCartInitial = Object.freeze({ type: 'cart_initial' })

/**
 * Holds the current step of a dispensary cart hydration.
 *
 * A `BehaviorSubject` rather than a signal so a subscriber that mounts mid-update reads the step
 * the update is actually in. With a non-replaying signal it read `cart_initial` and reported
 * not-loading instead, which matters because `updateCartDispensary` empties the cart before adding
 * the shared products (sc-142675).
 */
const dispensaryCartStateSubject$ = new BehaviorSubject<DispensaryCartState>(initialState)

export const dispensaryCartState$: Observable<DispensaryCartState> =
  dispensaryCartStateSubject$.asObservable()

export const setDispensaryCartState = (state: DispensaryCartState): void =>
  dispensaryCartStateSubject$.next(state)

/**
 * Return to the initial step so a new hydration inherits neither the previous one's `cart_final` nor
 * its error.
 */
export const resetDispensaryCartState = (): void => dispensaryCartStateSubject$.next(initialState)

/**
 * Which step the hydration is on, as reported by the last event to name one. `cart_initial` and
 * `cart_hydration_start` match both, so a reset and the head of a new hydration each restart the
 * pair rather than leaving the previous hydration's last step showing beside the new one's first.
 * `cart_error` matches neither, so a failure leaves the last step it reached in place and is
 * reported through `error` instead.
 */
const isHydrationHead = (event: DispensaryCartState): boolean =>
  event.type === 'cart_initial' || event.type === 'cart_hydration_start'

const isStartState = (event: DispensaryCartState): boolean =>
  isHydrationHead(event) || event.type.endsWith('_start') || event.type === 'cart_final'

const isCompleteState = (event: DispensaryCartState): boolean =>
  isHydrationHead(event) || event.type.endsWith('_complete') || event.type === 'cart_final'

/**
 * A step is in flight until the hydration reaches a terminal state, `cart_error` among them: the
 * failure is reported through `error` instead, and the consumers of the loading flag —
 * `ProtocolAddToCart`, `ItemControls`, `ReorderAll`, `OrderItemEntry` — would otherwise keep their
 * add-to-cart controls disabled until the next `resetDispensaryCartState()`.
 */
const isTerminalState = (event: DispensaryCartState): boolean =>
  event.type === 'cart_initial' || event.type === 'cart_final' || event.type === 'cart_error'

export interface DispensaryCartStatus {
  startState: DispensaryCartState
  completeState: DispensaryCartState
  loading: boolean
  called: boolean
  error: Error | undefined
}

const initialStatus: DispensaryCartStatus = {
  startState: initialState,
  completeState: initialState,
  loading: false,
  called: false,
  error: undefined,
}

/**
 * The whole status derived in one pass over the state, rather than by combining separately-derived
 * streams.
 *
 * `combineLatest` re-emits once per input, so one event that moves two fields was observed in a
 * combination that never existed: `cart_error` cleared the loading flag before the error itself
 * arrived, and React 17 does not batch across that, so `NonEmptyCartContextProvider` rendered the
 * empty-cart recovery page for a frame over the cart the failed hydration had just emptied.
 *
 * The error is read off the current state rather than accumulated, so it clears when the state moves
 * on. Held in a slot of its own it outlived the hydration that raised it: the next clean hydration
 * ran through to `cart_final` with `error` still truthy, and the provider checks its error branch
 * ahead of everything else. The failure is only reported here, never captured — `updateCartDispensary`
 * rethrows what it publishes and `useDispensaryUpdateSession` captures that through
 * `useLogAndCaptureError`, so capturing here too would file one failure twice.
 */
const dispensaryCartStatusChange$: Observable<DispensaryCartStatus> = dispensaryCartState$.pipe(
  scan(
    (status, event): DispensaryCartStatus => ({
      startState: isStartState(event) ? event : status.startState,
      completeState: isCompleteState(event) ? event : status.completeState,
      loading: !isTerminalState(event),
      called: event.type !== 'cart_initial',
      error: event.type === 'cart_error' ? event.error : undefined,
    }),
    initialStatus,
  ),
  // `scan` builds a new object per event, so collapse the ones that say nothing new — two resets in
  // a row, for instance, which is why `initialState` is a single shared reference
  distinctUntilChanged(
    (previous, next) =>
      previous.startState === next.startState &&
      previous.completeState === next.completeState &&
      previous.loading === next.loading &&
      previous.called === next.called &&
      previous.error === next.error,
  ),
)

export const [useDispensaryCartStatus, dispensaryCartStatus$] = bind(
  dispensaryCartStatusChange$,
  initialStatus,
)
